import { useQuery, useMutation } from '@tanstack/react-query';

import Axios from 'axios';
import { queryClient } from 'QueryClientProvider';

const getFollowup = (followupId) => Axios.get(`/followUps/${followupId}`);

const useFollowup = (followupId) => {
    return useQuery({
        queryKey: ['followup', followupId],
        queryFn: () => getFollowup(followupId),
        staleTime: 60000,
        cacheTime: 60000,
        retry: false,
        enabled: followupId ? true : false,
    });
};

const forceReloadFollowup = (ID) => {
    queryClient.invalidateQueries({ queryKey: ['followup', ID] });
};

const onDeleteFollowup = ({ ID }) => Axios.delete(`/followUps/${ID}`);

const useDeleteFollowup = () => {
    return useMutation({
        mutationFn: onDeleteFollowup,
        onSuccess: (data, variables) => {
            if (variables.reloadID && variables.reloadID !== variables.ID) {
                forceReloadFollowup(variables.reloadID);
            }
        },
    });
};

const onDeleteFollowupGroup = ({ GroupGUID }) => Axios.delete(`/followUps/deletegroup/${GroupGUID}`);

const useDeleteFollowupGroup = () => {
    return useMutation({
        mutationFn: onDeleteFollowupGroup,
        onSuccess: (data, variables) => {},
    });
};

const onDeleteFollowupPeriodGroup = ({ PeriodGroupGUID }) =>
    Axios.delete(`/followUps/deleteperiodgroup/${PeriodGroupGUID}`);

const useDeleteFollowupPeriodGroup = () => {
    return useMutation({
        mutationFn: onDeleteFollowupPeriodGroup,
        onSuccess: (data, variables) => {},
    });
};

const onUpdateFollowup = ({ followupData }) => Axios.put('/followUps', followupData);

const useUpdateFollowup = () => {
    return useMutation({
        mutationFn: onUpdateFollowup,
        onSuccess: (data, variables) => {
            forceReloadFollowup(variables.followupData.ID);
        },
    });
};

const onAddFollowup = ({ followupData }) => Axios.post('/followUps', followupData);

const useAddFollowup = () => {
    return useMutation({
        mutationFn: onAddFollowup,
        onSuccess: (data, variables) => {
            forceReloadFollowup(data.ID);
        },
    });
};

const onMoveFollowup = ({ followupData }) => Axios.put('/followUps/MoveEvent', followupData);

const useMoveFollowup = () => {
    return useMutation({
        mutationFn: onMoveFollowup,
        onSuccess: (data, variables) => {
            forceReloadFollowup(variables.followupData.ID);
        },
    });
};

const onMoveGroupedFollowup = ({ followupData }) => Axios.put('/followUps/MoveGroupedEvent', followupData);

const useMoveGroupedFollowup = () => {
    return useMutation({
        mutationFn: onMoveGroupedFollowup,
        onSuccess: (data, variables) => {
            forceReloadFollowup(variables.followupData.ID);
        },
    });
};

const onUpdateAcknowledgment = ({ ID }) => Axios.put(`/followUps/acknowledgment/${ID}`);

const useUpdateFollowupAcknowledgment = () => {
    return useMutation({
        mutationFn: onUpdateAcknowledgment,
        onSuccess: (data, variables) => {
            forceReloadFollowup(variables.followupId);
        },
    });
};

const onCancelFollowups = ({ ID }) => Axios.put(`/followUps/cancellation/${ID}`);

const useCancelFollowup = () => {
    return useMutation({
        mutationFn: onCancelFollowups,
        onSuccess: (data, variables) => {
            forceReloadFollowup(variables.ID);
        },
    });
};

const onCloneModelsFollowup = ({ data }) => Axios.post(`/followups/CloneForModels`, data);

const useCloneModelsFollowup = () => {
    return useMutation({
        mutationFn: onCloneModelsFollowup,
        onSuccess: (data, variables) => {
            forceReloadFollowup(variables.data.EventID);
        },
    });
};

const onUpdateGroupedIndividualFollowUp = ({ followupData }) => Axios.put('/FollowUps/GroupedIndividual', followupData);

const useUpdateGroupedIndividualFollowUp = () => {
    return useMutation({
        mutationFn: onUpdateGroupedIndividualFollowUp,
        onSuccess: (data, variables) => {
            forceReloadFollowup(variables.followupData.ID);
        },
    });
};

const onUpdatePeriodGroupedIndividualFollowUp = ({ followupData }) =>
    Axios.put('/FollowUps/PeriodGroupedIndividual', followupData);

const useUpdatePeriodGroupedIndividualFollowUp = () => {
    return useMutation({
        mutationFn: onUpdatePeriodGroupedIndividualFollowUp,
        onSuccess: (data, variables) => {
            forceReloadFollowup(variables.followupData.ID);
        },
    });
};

const onUpdateGroupedCommonFollowUp = ({ followupData }) => Axios.put('/FollowUps/GroupedCommon', followupData);

const useUpdateGroupedCommonFollowUp = () => {
    return useMutation({
        mutationFn: onUpdateGroupedCommonFollowUp,
        onSuccess: (data, variables) => {
            forceReloadFollowup(variables.followupData.ID);
        },
    });
};

const onUpdatePeriodGroupedCommonFollowUp = ({ followupData }) =>
    Axios.put('/FollowUps/PeriodGroupedCommon', followupData);

const useUpdatePeriodGroupedCommonFollowUp = () => {
    return useMutation({
        mutationFn: onUpdatePeriodGroupedCommonFollowUp,
        onSuccess: (data, variables) => {
            forceReloadFollowup(variables.followupData.ID);
        },
    });
};

const onAddFollowupPeriods = ({ followUpID, periodsData }) =>
    Axios.post(`/followUps/AddPeriods/${followUpID}`, periodsData);

const useAddFollowupPeriods = () => {
    return useMutation({
        mutationFn: onAddFollowupPeriods,
        onSuccess: (data, variables) => {
            forceReloadFollowup(variables.followUpID);
        },
    });
};

export {
    useFollowup,
    getFollowup,
    forceReloadFollowup,
    useAddFollowup,
    useAddFollowupPeriods,
    useCancelFollowup,
    useCloneModelsFollowup,
    useDeleteFollowup,
    useDeleteFollowupGroup,
    useDeleteFollowupPeriodGroup,
    useMoveFollowup,
    useMoveGroupedFollowup,
    useUpdateFollowup,
    useUpdateFollowupAcknowledgment,
    useUpdateGroupedIndividualFollowUp,
    useUpdateGroupedCommonFollowUp,
    useUpdatePeriodGroupedIndividualFollowUp,
    useUpdatePeriodGroupedCommonFollowUp,
};
